import { useState, useContext, useEffect, useRef } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { Box } from "@mui/material";
import { AmountBox, AppAmountBoxWrapper, FilterNonIdeal } from "../../../common";
import { AppPagination } from "../../../../Libraries";
import ItemList from "./ItemList";
import CashBookListTableHeader from "./CashBookListTableHeader";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { useCashBookList } from "../../../../hooks/useCashBookList";
import { ITEMS_PER_PAGE, NEW_SORT } from "../../../../constants";
import AppTableFilters from "../../../common/AppTableFilters";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import withObservables from "@nozbe/with-observables";
import { of } from "rxjs";
import { buildFiltersConfig, getCategoriesByType } from "../../../../utils";

const CASH_BOOK_FILTERS = ["View", "Category", "Date", "Sort", "Amount", "Keywords"];

const getViewOptions = (I18n) => [
  { label: I18n.cash_in, value: "Cash In", type: "in" },
  { label: I18n.cash_out, value: "Cash Out", type: "out" },
];


const getCashBookCategories = () => {
  const cashIn = getCategoriesByType("cb_cash_in");
  const cashOut = getCategoriesByType("cb_cash_out");
  const cashInValues = new Set(cashIn.map((c) => c.value));
  return [...cashIn, ...cashOut.filter((c) => !cashInValues.has(c.value))];
};

const CashBookList = ({
  accountWrapperClassName,
  accountListBodyWarpperClassName,
  user,
  business,
  customers,
  initialCashTxns,
} = {}) => {
  const [pageSize, setPageSize] = useState(0);
  const [selectedContactId, setSelectedContactId] = useState("");
  const justClosedDropdownRef = useRef(false);
  const [I18n] = useContext(LanguageContext);

  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
  } = useFiltersHandler(undefined, I18n);

  const {
    cashTxnList,
    handleCashTxn,
    totalCashTxnFilterCount,
    totalMoneyIn,
    totalMoneyOut,
    isLoading,
    balanceMap,
  } = useCashBookList(initialCashTxns, ITEMS_PER_PAGE);

  const initialGetData = (forceRecalculateBalances = false) => {
    setPageSize(0);
    handleCashTxn({ business, filters, offset: 0, limit: ITEMS_PER_PAGE, forceRecalculateBalances });
  };

  useEffect(() => {
    initialGetData();
  }, [filters]);

  return (
    <Box id="cash_book_list_wrapper" className={accountWrapperClassName || "AccountWrapper"}>
      <AppTableFilters
        filters={CASH_BOOK_FILTERS}
        onFilterChange={handleFilterChange}
        appliedChips={appliedChips}
        filterState={filters}
        onRemoveChip={(chip) => handleRemoveChip(chip)}
        filtersConfig={buildFiltersConfig(CASH_BOOK_FILTERS, getCashBookCategories(), undefined, undefined, undefined, undefined, undefined, getViewOptions(I18n), undefined, I18n)}
        isResetFilter={isResetFilter}
        setIsResetFilter={setIsResetFilter}
      />

      <AppAmountBoxWrapper>
        <AmountBox
          data={[
            {
              amount: formatNum(totalMoneyIn),
              title: I18n.total_cash_in,
              id: "total_cash_in",
              rsLabel: true,
              amountParaTextClassname: "foundationGreenColor",
            },
            {
              amount: formatNum(totalMoneyOut),
              title: I18n.total_cash_out,
              id: "total_cash_out",
              rsLabel: true,
              amountParaTextClassname: "newFontColor",
            },
            {
              amount: formatNum(Math.abs(totalMoneyIn - totalMoneyOut)),
              title: I18n.net_balance,
              id: "net_balance",
              rsLabel: true,
              amountParaTextClassname: "newFontColor",
            },
          ]}
        />
      </AppAmountBoxWrapper>

      <CashBookListTableHeader
        isAscending={filters.sort !== NEW_SORT.Oldest}
        disabled={!cashTxnList.length}
        handleSort={() => {
          handleFilterChange("sort", filters.sort === NEW_SORT.Oldest ? NEW_SORT.Newest : NEW_SORT.Oldest);
        }}
      />

      <Box
        className={`${accountListBodyWarpperClassName || "accountListBodyWarpper"} generic-scrollbar`}
        id="cashBookScrollableDiv"
      >
        {cashTxnList.length > 0 ? (
          <AppPagination
            data={{
              handleNext: () => {
                if (cashTxnList.length < totalCashTxnFilterCount) {
                  setPageSize((v) => v + 1);
                  handleCashTxn({
                    business,
                    offset: (pageSize + 1) * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE,
                    filters,
                    isCalculateTotal: false,
                  });
                }
              },
              pageLoad: true,
              count: totalCashTxnFilterCount,
              list: cashTxnList,
              scrollableTarget: "cashBookScrollableDiv",
            }}
          >
            {cashTxnList.map((item, index) => (
              <ItemList
                key={item.id || index}
                index={index}
                cashTxn={item}
                business={business}
                refetchList={initialGetData}
                contactList={customers || []}
                selectedContactId={selectedContactId}
                setSelectedContactId={setSelectedContactId}
                justClosedDropdownRef={justClosedDropdownRef}
                balance={balanceMap?.[item.id] || 0}
              />
            ))}
          </AppPagination>
        ) : !isLoading ? (
          <Box id="cash_book_filter_non_ideal_wrapper" className="h-100">
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={I18n.try_adjusting_filters}
              subTitleTextOnClick={() => resetFilters()}
              subTitleText={I18n.reset_small}
              subTitleTextId="cash_book_filter_reset"
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const enhance = withObservables(["business"], ({ business }) => ({
  initialCashTxns: business ? business.getCashTxn(undefined, undefined, "").observe() : of([]),
  customers: business ? business.getAllCustomers.observe() : of([]),
}));

export default enhance(CashBookList);
